import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ScanFace, Search, CheckCircle2, XCircle, AlertTriangle, Download, ShieldCheck, ChevronDown, Check, Clock, Copy, RefreshCw, Fingerprint } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import api from "lib/api";
import { livenessReportUrl } from "lib/media";
import { useI18n } from "i18n";

const STATUS_CLS = {
  passed:  "bg-emerald-400/10 text-emerald-500 border-emerald-400/20",
  failed:  "bg-red-400/10 text-red-500 border-red-400/20",
  pending: "bg-amber-400/10 text-amber-500 border-amber-400/20",
  expired: "bg-muted text-muted-foreground border-border",
};

const LOCALE_TAG = { pt: "pt-BR", en: "en-US", es: "es-ES", it: "it-IT" };
const STATUS_KEYS = ["passed", "failed", "pending", "expired"];
const DEFAULT_STATUSES = ["passed", "failed", "pending"]; // all but Expired

function CopyId({ id }) {
  const [ok, setOk] = useState(false);
  const { t } = useI18n();
  return (
    <button type="button" title={t("sessions.copyId")}
      onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(id); setOk(true); setTimeout(() => setOk(false), 1200); }}
      className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center h-6 w-6 rounded shrink-0 text-muted-foreground hover:text-primary hover:bg-primary/10">
      {ok ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function SignedCell({ status }) {
  const { t } = useI18n();
  if (status === "yes")
    return <span className="inline-flex items-center gap-1.5 text-emerald-500 text-sm"><ShieldCheck className="h-4 w-4" /> {t("sessions.yes")}</span>;
  if (status === "error")
    return <span className="inline-flex items-center gap-1.5 text-red-500 text-sm"><XCircle className="h-4 w-4" /> {t("sessions.signedError")}</span>;
  return <span className="inline-flex items-center gap-1.5 text-amber-500 text-sm"><Clock className="h-4 w-4" /> {t("sessions.signedWaiting")}</span>;
}

function Approved({ status, deepFlagged }) {
  const { t } = useI18n();
  if (status === "passed") {
    return (
      <span className="inline-flex items-center gap-1.5 text-emerald-500 text-sm">
        <CheckCircle2 className="h-4 w-4" /> {t("sessions.yes")}
        {deepFlagged && (
          <span title={t("sessions.deepFlaggedTip")}>
            <AlertTriangle className="h-4 w-4 text-amber-500" />
          </span>
        )}
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="inline-flex items-center gap-1.5 text-red-500 text-sm">
        <XCircle className="h-4 w-4" /> {t("sessions.no")}
      </span>
    );
  }
  return <span className="text-sm text-muted-foreground">—</span>;
}

// Admin → Verificações: every liveness verification, newest first. Clicking a
// row opens the session review in a NEW TAB.
export default function Sessions() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  // Seed the filter/search from the URL so returning to this page (browser Back)
  // restores exactly the filter that was applied.
  const [statuses, setStatuses] = useState(() => {
    const s = searchParams.get("status");
    return s ? s.split(",").filter(Boolean) : DEFAULT_STATUSES;
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const debounceRef = useRef(null);

  // `silent` polls in the background without flipping the loading spinner (which
  // would blank the table); React reconciles only the cells that actually changed.
  const fetchRows = useCallback(async (statusF, query, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusF) params.set("status", statusF);
      if (query) params.set("q", query);
      const res = await api.get(`/api/sessions/?${params.toString()}`);
      setRows(res.data.results || []);
      setCount(res.data.count || 0);
    } catch {
      /* ignore */
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(statuses.join(","), q); }, [fetchRows]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reflect the applied filter/search in the URL (replace, so keystrokes don't spam history)
  // — this is what makes the filter survive a round-trip into a session and back.
  useEffect(() => {
    const p = {};
    if (statuses.length) p.status = statuses.join(",");
    if (q) p.q = q;
    setSearchParams(p, { replace: true });
  }, [statuses, q, setSearchParams]);

  // Auto-refresh: silently re-poll the current filter/search every few seconds so
  // "Awaiting PAD" rows flip to signed on their own. Reads the latest filter/query
  // from a ref so typing/filtering never resets the timer, and pauses when the tab
  // is hidden.
  const pollArgs = useRef({ statuses: DEFAULT_STATUSES, q: "" });
  useEffect(() => { pollArgs.current = { statuses, q }; }, [statuses, q]);
  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      const { statuses: s, q: query } = pollArgs.current;
      fetchRows(s.join(","), query, true);
    }, 8000);
    return () => clearInterval(id);
  }, [fetchRows]);

  const toggleStatus = (k) => {
    const next = statuses.includes(k) ? statuses.filter((s) => s !== k) : [...statuses, k];
    setStatuses(next);
    fetchRows(next.join(","), q);
  };
  const onSearch = (v) => {
    setQ(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchRows(statuses.join(","), v), 350);
  };

  const openDetail = (id) => navigate(`/liveness/sessions/${id}`);

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString(LOCALE_TAG[lang] || "en-US",
    { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

  return (
    <div className="animate-fade-in w-full">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <ScanFace className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold tracking-tight">{t("sessions.title")}</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{t("sessions.loadedCount").replace("{n}", count)}</p>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => fetchRows(statuses.join(","), q)} disabled={loading}
            title={t("sessions.refresh", "Atualizar")} aria-label={t("sessions.refresh", "Atualizar")}
            className="h-10 inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 text-sm hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">{t("sessions.refresh", "Atualizar")}</span>
          </button>
          <div className="relative">
            <button type="button" onClick={() => setFilterOpen((o) => !o)}
              className="h-10 inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40">
              {t("sessions.filterStatus")}
              <span className="text-xs text-muted-foreground">({statuses.length}/{STATUS_KEYS.length})</span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>
            {filterOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setFilterOpen(false)} />
                <div className="absolute right-0 mt-1 z-20 w-48 rounded-lg border border-border bg-background shadow-lg py-1">
                  {STATUS_KEYS.map((k) => {
                    const on = statuses.includes(k);
                    return (
                      <button key={k} type="button" onClick={() => toggleStatus(k)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/50 text-left">
                        <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-primary border-primary text-primary-foreground" : "border-border"}`}>
                          {on && <Check className="h-3 w-3" />}
                        </span>
                        {t(`status.${k}`)}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input value={q} onChange={(e) => onSearch(e.target.value)}
              placeholder={t("sessions.searchPlaceholder")}
              className="h-10 w-64 rounded-lg border border-border bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12">
              <ScanFace className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t("sessions.empty")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground uppercase text-xs tracking-wider">{t("sessions.colDate")}</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground uppercase text-xs tracking-wider">{t("sessions.colParticipant")}</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground uppercase text-xs tracking-wider">{t("sessions.colSession")}</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground uppercase text-xs tracking-wider">{t("sessions.colStatus")}</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground uppercase text-xs tracking-wider">{t("sessions.colApproved")}</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground uppercase text-xs tracking-wider">{t("sessions.colSigned")}</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground uppercase text-xs tracking-wider">{t("sessions.colReport")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const cls = STATUS_CLS[r.status] || STATUS_CLS.pending;
                    return (
                      <tr key={r.id} onClick={() => openDetail(r.id)}
                        className="group border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer">
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmt(r.created)}</td>
                        <td className="px-4 py-3">
                          {r.subject_name || r.subject_email ? (
                            <>
                              <span className="block font-medium text-primary">{r.subject_name || "—"}</span>
                              <span className="block text-muted-foreground text-xs">{r.subject_email || ""}</span>
                            </>
                          ) : r.biometric_person_id ? (
                            // No declared subject: show who the FACE was matched to (biometrics).
                            <>
                              <span className="block font-medium text-primary truncate">
                                {r.biometric_person_name || r.biometric_person_id.slice(0, 8)}
                              </span>
                              <span className="inline-flex items-center gap-1 text-muted-foreground text-xs">
                                <Fingerprint className="h-3 w-3" />
                                {r.authenticated ? t("sessions.bioMatch", "Match biométrico") : t("sessions.bioIdentified", "Identificado")}
                              </span>
                            </>
                          ) : (
                            <span className="block font-medium text-primary">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="min-w-0">
                              <span className="block text-sm text-foreground truncate">{r.company_name || "—"}</span>
                              <span className="block font-mono text-xs text-muted-foreground">{r.id.slice(0, 8)}…</span>
                            </div>
                            <CopyId id={r.id} />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${cls}`}>
                            {t(`status.${r.status}`, t("status.pending"))}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <Approved status={r.status} deepFlagged={r.deep_flagged} />
                        </td>
                        <td className="px-4 py-3">
                          <SignedCell status={r.signed} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            title={t("report.download")}
                            aria-label={t("report.download")}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(livenessReportUrl(r.id), "_blank", "noopener");
                            }}
                            className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
                            <Download className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
